/**
 * `/l/:token` — the hosted lead brief a contractor opens from an email or
 * SMS (T062). Mobile-first, no login: the token is the credential.
 *
 * Opening it never changes anything (`GET /l/:token` is render-only, so an
 * email scanner prefetching the link cannot accept a lead). Accept / Decline
 * / I called are explicit POSTs; the first response wins (compare-and-set on
 * the server) and invalid, expired and already-used links all look the same.
 *
 * #869: Accept and "I called" need the contact rules ticked first. The phone
 * is not on the page until then; the server returns it with the answer.
 * Accept stays tappable: a tap without the tick says why next to the box and
 * moves focus there, where a greyed-out button said nothing. "I called them"
 * is only offered where the page already shows the phone (a server from
 * before #869); nobody can have called a number they have not been shown.
 */
import { useRef, useState } from 'react';
import { useSession } from '../auth/session';
import type { BriefActionResponse } from '../api/types';
import { ErrorNotice, Loading, formatDateTime, humanize } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const FAIL_COPY: Record<string, string> = {
  expired: 'This request has expired and was offered to someone else.',
  used: 'Someone already responded to this request.',
  not_found: 'This link is no longer valid.',
  rules_not_acknowledged: 'Tick the box to agree to the contact rules first.',
};

/** `(206) 555-0143` for a US number in any common form; anything else as given. */
function formatPhone(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : raw;
}

function telHref(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return digits.length === 10 ? `tel:+1${digits}` : `tel:${raw.replace(/[^\d+]/g, '')}`;
}

export function LeadBrief({ token }: { token: string }) {
  const { api } = useSession();
  const brief = useQuery(() => api.getBrief(token), [token]);
  const [done, setDone] = useState<BriefActionResponse | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [rulesTicked, setRulesTicked] = useState(false);
  // Set by a tap on Accept / I called before the tick; cleared by the tick.
  const [rulesNudge, setRulesNudge] = useState(false);
  const rulesBoxRef = useRef<HTMLInputElement>(null);
  // Absent from a server that predates #869 (the portal can deploy first);
  // that server does not ask for them, so the buttons work as before.
  const rules = brief.data && brief.data.valid ? brief.data.contact_rules : undefined;
  const rulesOk = !rules || rulesTicked;
  const act = useMutation(async (action: 'accept' | 'decline' | 'contacted') => {
    const res = await api.briefAction(token, action, {
      reason: action === 'decline' && reason.trim() ? reason.trim() : undefined,
      contactRulesVersion: action !== 'decline' && rules && rulesTicked ? rules.version : undefined,
    });
    setDone(res);
    return res;
  });
  function answer(action: 'accept' | 'contacted') {
    if (!rulesOk) {
      setRulesNudge(true);
      rulesBoxRef.current?.focus();
      return;
    }
    void act.run(action);
  }

  return (
    <main className="brief" id="main">
      <div className="eyebrow">FinderlyFix · job request</div>
      {brief.loading ? (
        <Loading />
      ) : brief.error ? (
        <ErrorNotice error={brief.error} />
      ) : !brief.data || !brief.data.valid ? (
        <>
          <h1 id="page-title" tabIndex={-1}>This link is no longer valid</h1>
          <p className="muted">It may have expired, or the job was already taken.</p>
        </>
      ) : (
        (() => {
          const l = brief.data.lead;
          const c = done && done.ok && done.contact ? done.contact : l.contact;
          const showNudge = rulesNudge && !rulesTicked;
          return (
            <>
              <span className={`tag urg ${l.urgency === 'emergency' ? 'bad' : l.urgency === 'urgent' ? 'warn' : ''}`}>{humanize(l.urgency)}</span>
              {l.diagnosis_status === 'unavailable_busy' ? <span className="tag warn">Not diagnosed</span> : null}
              <h1 id="page-title" tabIndex={-1}>
                {l.problem_label ?? humanize(l.trade)}
              </h1>
              {l.summary ? <p>{l.summary}</p> : null}
              <section className="panel panel-pad">
                <dl className="facts">
                  <dt>Trade</dt>
                  <dd>{humanize(l.trade)}</dd>
                  {l.cost_estimate && (l.cost_estimate.low || l.cost_estimate.high) ? (
                    <>
                      <dt>Estimate</dt>
                      <dd>
                        ${l.cost_estimate.low ?? '?'}–${l.cost_estimate.high ?? '?'}
                      </dd>
                    </>
                  ) : null}
                  <dt>Homeowner</dt>
                  <dd>{c.first_name ?? 'Shown once you accept'}</dd>
                  <dt>Phone</dt>
                  <dd>{c.phone_1 ? <a href={telHref(c.phone_1)}>{formatPhone(c.phone_1)}</a> : 'Shown once you accept'}</dd>
                  <dt>Address</dt>
                  <dd>
                    {[c.address_1, c.city, c.state, c.postal_code].filter(Boolean).join(', ') || '—'}
                    {c.address_1 ? null : <span className="muted"> (street shown once you accept)</span>}
                  </dd>
                  {brief.data.sla_expires_at ? (
                    <>
                      <dt>Respond by</dt>
                      <dd>{formatDateTime(brief.data.sla_expires_at)}</dd>
                    </>
                  ) : null}
                </dl>
              </section>
              {done ? (
                <>
                  <div className={`notice ${done.ok ? 'ok' : 'bad'}`} role="status">
                    {done.ok ? `Thanks — recorded as ${done.status}.` : FAIL_COPY[done.reason]}
                  </div>
                  {done.ok && c.phone_1 ? (
                    <div className="stack">
                      <a className="btn btn-accent btn-lg" href={telHref(c.phone_1)}>
                        Call now · {formatPhone(c.phone_1)}
                      </a>
                      {/* The rules still apply once the number is on screen:
                          the same text, shown where the call starts. */}
                      {rules ? (
                        <section className="sunk" aria-labelledby="contact-rules-reminder">
                          <strong id="contact-rules-reminder">Your contact rules</strong>
                          <ul className="muted">
                            {rules.rules.map((rule) => (
                              <li key={rule}>{rule}</li>
                            ))}
                          </ul>
                        </section>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : declining ? (
                <div className="stack">
                  <label className="field">
                    <span className="label">Why not? (optional)</span>
                    <input className="input" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
                  </label>
                  <div className="actions">
                    <button type="button" className="btn btn-primary" disabled={act.pending} onClick={() => void act.run('decline')}>
                      Decline this job
                    </button>
                    <button type="button" className="btn" onClick={() => setDeclining(false)}>
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                <div className="stack">
                  {rules ? (
                    <section className="panel panel-pad" aria-labelledby="contact-rules-title">
                      <h2 id="contact-rules-title">Before you contact the homeowner</h2>
                      <ul>
                        {rules.rules.map((rule) => (
                          <li key={rule}>{rule}</li>
                        ))}
                      </ul>
                      <label className="check" htmlFor="contact-rules-ack">
                        <input
                          id="contact-rules-ack"
                          ref={rulesBoxRef}
                          type="checkbox"
                          checked={rulesTicked}
                          aria-describedby={showNudge ? 'contact-rules-hint' : undefined}
                          aria-invalid={showNudge || undefined}
                          onChange={(e) => {
                            setRulesTicked(e.target.checked);
                            if (e.target.checked) setRulesNudge(false);
                          }}
                        />
                        <span>{rules.acknowledgement}</span>
                      </label>
                      {showNudge ? (
                        <p id="contact-rules-hint" className="notice bad" role="alert">
                          {FAIL_COPY.rules_not_acknowledged}
                        </p>
                      ) : null}
                    </section>
                  ) : null}
                  <div className="actions">
                    <button type="button" className="btn btn-accent" disabled={act.pending} onClick={() => answer('accept')}>
                      {act.pending ? 'Sending…' : 'Accept job'}
                    </button>
                    {l.contact.phone_1 ? (
                      <button type="button" className="btn" disabled={act.pending} onClick={() => answer('contacted')}>
                        I called them
                      </button>
                    ) : null}
                    <button type="button" className="btn btn-ghost" disabled={act.pending} onClick={() => setDeclining(true)}>
                      Decline
                    </button>
                  </div>
                </div>
              )}
              <ErrorNotice error={act.error} />
            </>
          );
        })()
      )}
    </main>
  );
}
